import type { TError, TVideoPlayedBlocks } from '@onesource/schemas';
import { useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/**
 * Query key for fetching the user's played-video map of a lesson - non-hook version
 */
export const getVideoPlayedBlocksQueryKey = (lessonId: string) => [ENTITY, 'video-played', lessonId];

/**
 * Query key for fetching the user's played-video map of a lesson
 */
export const useGetVideoPlayedBlocksQueryKey = (lessonId: string) => getVideoPlayedBlocksQueryKey(lessonId);

/**
 * Hook to fetch which video blocks of a lesson the current user has actually
 * started playing (backed by the VIDEO_PLAY activity log). Returns a map of
 * content block ID -> true; never-played blocks are absent. Combined with the
 * watch counts (finished views) this identifies "started but not finished"
 * videos for the CONTINUE WATCHING state.
 *
 * @param lessonId - Lesson ID (UUID)
 */
export const useGetVideoPlayedBlocks = (lessonId: string) => {
  return useQuery<TVideoPlayedBlocks, TError>({
    queryKey: useGetVideoPlayedBlocksQueryKey(lessonId),
    queryFn: async () => {
      const res = await axios.get<TVideoPlayedBlocks>(`/community-hub/lessons/${lessonId}/video-played`);
      return res.data;
    },
    enabled: !!lessonId,
    staleTime: 60 * 1000, // 1 minute (moves as the user starts videos)
  });
};
